// ── COSTRUZIONE TESTI AUTOMATICI ─────────────────────────────────────────────

import { PdfInfo, PaymentInfo } from './pdf-types';
import { methodLabel } from './pdf-variables';

/** Testo dell'importo dell'acconto: "di €150" se fisso, "pari al 15% del totale" se percentuale. */
function depositAmountText(pay: PaymentInfo, lang: string): string {
  if (pay.deposit_type === 'percent') {
    return lang === 'it'
      ? `pari al ${pay.deposit_amount}% del totale`
      : `equal to ${pay.deposit_amount}% of the total`;
  }
  return lang === 'it' ? `di €${pay.deposit_amount}` : `of €${pay.deposit_amount}`;
}

export function buildPaymentText(info: PdfInfo, lang: string): string {
  const pay = info.payment;
  if (!pay) return '';

  const depMethod = methodLabel(pay.deposit_method, lang);
  const balMethod = methodLabel(pay.final_payment_method, lang);

  let when: string;
  if (lang === 'it') {
    when = pay.final_payment_when === 'wedding_day'
      ? 'il giorno del matrimonio'
      : `${pay.final_payment_days} giorni prima del matrimonio`;
  } else {
    when = pay.final_payment_when === 'wedding_day'
      ? 'on the wedding day'
      : `${pay.final_payment_days} days before the wedding`;
  }

  let transferDetails = '';
  const needsTransfer =
    pay.deposit_method === 'transfer' || pay.deposit_method === 'both' ||
    pay.final_payment_method === 'transfer' || pay.final_payment_method === 'both';

  if (needsTransfer) {
    if (pay.iban) transferDetails += ` IBAN: ${pay.iban}.`;
    if (pay.paypal) transferDetails += ` PayPal: ${pay.paypal}.`;
  }

  const depositText = depositAmountText(pay, lang);

  if (lang === 'it') {
    return `Per prenotare i servizi, il Cliente dovrà versare un acconto ${depositText} ${depMethod}.${transferDetails} L'acconto verrà detratto dall'importo totale. Il saldo dovrà essere corrisposto ${when} ${balMethod}.`;
  }

  return `In order to book the services, the Client needs to pay a deposit ${depositText} ${depMethod}.${transferDetails} The deposit will be deducted from the total amount. The remaining balance is required to be paid ${when} ${balMethod}.`;
}

export function buildLocationText(info: PdfInfo, lang: string): string {
  const loc = info.location;
  if (!loc) return '';

  const reqs: string[] = [];
  if (loc.needs_table) reqs.push(lang === 'it' ? 'un tavolo da lavoro' : 'a work table');
  if (loc.needs_natural_light) reqs.push(lang === 'it' ? 'luce naturale adeguata' : 'adequate natural light');
  if (loc.needs_power_outlet) reqs.push(lang === 'it' ? 'una presa elettrica' : 'a power outlet');

  const base = reqs.length
    ? lang === 'it'
      ? `Per l'erogazione dei servizi sono necessari in loco i seguenti elementi: ${reqs.join(', ')}.`
      : `The following elements are required on site to deliver the services: ${reqs.join(', ')}.`
    : '';

  const notes = loc.extra_notes || loc.notes || '';
  return notes ? `${base} ${notes}` : base;
}

export function buildCancellationText(info: PdfInfo, lang: string): string {
  const can = info.cancellation;
  if (!can) return '';

  let refund: string;
  let postpone: string;

  if (lang === 'it') {
    refund = can.deposit_refundable ? 'sarà rimborsato' : 'non sarà rimborsato';
    postpone = can.postponement_policy === 'transfer_date'
      ? "In caso di rinvio, l'acconto verrà trasferito alla nuova data."
      : "In caso di rinvio, l'acconto non sarà rimborsato.";
  } else {
    refund = can.deposit_refundable ? 'will be refunded' : 'will not be refunded';
    postpone = can.postponement_policy === 'transfer_date'
      ? 'In case of postponement, the deposit will be transferred to the new date.'
      : 'In case of postponement, the deposit will not be refunded.';
  }

  const extra = can.extra_notes || can.notes ? ` ${can.extra_notes || can.notes}` : '';

  if (lang === 'it') {
    return `In caso di cancellazione, l'acconto ${refund}. ${postpone}${extra}`;
  }
  return `In case of cancellation, the deposit ${refund}. ${postpone}${extra}`;
}

export function buildTitleText(info: PdfInfo, lang: string): string {
  if (info.business_name && info.full_name) {
    if (lang === 'it') {
      return `${info.business_name} — fornitor* di servizi per eventi, rappresentat* da ${info.full_name}, di seguito denominat* "FORNITORE"`;
    }
    return `${info.business_name} — event services provider, represented by ${info.full_name}, hereinafter referred to as "FORNITORE"`;
  }
  return info.business_name || info.full_name || '';
}
